import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';

import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { CreditCard, Building2, Loader2, CheckCircle, Copy, Mail, Share2, Users, Briefcase, UserCircle, Send, CalendarDays, Repeat } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Composant pour les paiements et partages de conventions
 * 
 * @param {object} convention - La convention concernée
 * @param {function} getAuthHeader - Fonction pour obtenir le header d'auth
 * @param {function} onSuccess - Callback après succès
 */
export default function ConventionPaymentShare({ 
  convention,
  getAuthHeader,
  onSuccess,
  open,
  onOpenChange
}) {
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [paymentFrequency, setPaymentFrequency] = useState('one_time');
  const [showBankInfo, setShowBankInfo] = useState(false);
  const [bankInfo, setBankInfo] = useState(null);
  const [shareMethod, setShareMethod] = useState('email');
  const [shareEmail, setShareEmail] = useState('');
  const [activeTab, setActiveTab] = useState('payment'); // 'payment' ou 'share'

  const montant = convention?.montant_mensuel || convention?.montant_total || 0;
  const isPaid = convention?.payment_status === 'paid';
  const isPendingVirement = convention?.payment_status === 'pending_virement';

  // Calculer le montant affiché selon la fréquence
  const getDisplayAmount = () => {
    if (paymentFrequency === 'monthly') {
      const monthly = (montant / 12).toFixed(2);
      return {
        amount: parseFloat(monthly),
        label: `${monthly}€/mois`,
        description: "12 mensualités (engagement 12 mois)"
      };
    } else if (paymentFrequency === 'annual') {
      return {
        amount: montant,
        label: `${montant.toFixed(2)}€/an`,
        description: "Renouvelé automatiquement chaque année"
      };
    }
    return {
      amount: montant,
      label: `${montant.toFixed(2)}€`,
      description: "Paiement unique"
    };
  };

  const displayAmount = getDisplayAmount();

  // Paiement Stripe
  const handleStripeCheckout = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/api/convention-payments/checkout/stripe`,
        { 
          convention_id: convention.id,
          origin_url: window.location.origin,
          payment_frequency: paymentFrequency
        },
        { headers: getAuthHeader() }
      );
      
      if (response.data.checkout_url) {
        toast.success('Redirection vers le paiement...', { duration: 2000 });
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du paiement');
    }
    setLoading(false);
  };

  // Initier virement
  const handleInitiateVirement = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/api/convention-payments/virement/initiate`,
        { 
          convention_id: convention.id,
          payment_frequency: paymentFrequency 
        },
        { headers: getAuthHeader() }
      );
      
      setBankInfo(response.data.bank_info);
      setShowBankInfo(true);
      toast.success('Informations bancaires générées');
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'initialisation du virement');
    }
    setLoading(false);
  };

  // Copier les infos bancaires
  const copyBankInfo = () => {
    if (!bankInfo) return;
    const text = `
Bénéficiaire: ${bankInfo.beneficiaire}
IBAN: ${bankInfo.iban}
BIC: ${bankInfo.bic}
Banque: ${bankInfo.banque}
Référence: ${bankInfo.reference}
Montant: ${bankInfo.montant}€
    `.trim();
    navigator.clipboard.writeText(text);
    toast.success('Informations copiées !');
  };

  // Partager convention
  const handleShare = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        share_to: shareMethod
      });
      if (shareMethod === 'email' && shareEmail) {
        params.append('email', shareEmail);
      }

      const response = await axios.post(
        `${API}/api/convention-payments/share/${convention.id}?${params.toString()}`,
        {},
        { headers: getAuthHeader() }
      );
      
      toast.success(response.data.message);
      if (onSuccess) onSuccess();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du partage');
    }
    setLoading(false);
  };

  if (!convention) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {montant > 0 ? (
              <CreditCard className="w-5 h-5 text-blue-500" />
            ) : (
              <Share2 className="w-5 h-5 text-purple-500" />
            )}
            {montant > 0 ? 'Paiement & Partage' : 'Partager la Convention'}
          </DialogTitle>
          <DialogDescription>
            {convention.titre}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {/* Onglets si montant > 0 */}
          {montant > 0 && (
            <div className="flex gap-2 mb-4 border-b">
              <button
                onClick={() => setActiveTab('payment')}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === 'payment' 
                    ? 'border-b-2 border-blue-500 text-blue-600' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <CreditCard className="w-4 h-4 inline mr-2" />
                Paiement
              </button>
              <button
                onClick={() => setActiveTab('share')}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === 'share' 
                    ? 'border-b-2 border-purple-500 text-purple-600' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Share2 className="w-4 h-4 inline mr-2" />
                Partager
              </button>
            </div>
          )}

          {/* Section Paiement */}
          {(activeTab === 'payment' && montant > 0) && (
            <div className="space-y-4">
              {/* Statut */}
              {isPaid && (
                <div className="bg-green-50 border border-green-200 p-4 rounded-lg flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-500" />
                  <div>
                    <p className="font-semibold text-green-700">Convention payée</p>
                    <p className="text-sm text-green-600">Le paiement a été validé</p>
                  </div>
                </div>
              )}

              {isPendingVirement && (
                <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                  <p className="font-semibold text-yellow-700">Virement en attente de validation</p>
                  <p className="text-sm text-yellow-600">
                    Référence: {convention.virement_reference}
                  </p>
                </div>
              )}

              {!isPaid && !isPendingVirement && (
                <>
                  {/* Choix de la fréquence de paiement */}
                  <div className="space-y-3">
                    <Label className="font-semibold">Fréquence de paiement</Label>
                    <RadioGroup value={paymentFrequency} onValueChange={setPaymentFrequency}>
                      <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                        <RadioGroupItem value="one_time" id="freq-one_time" />
                        <Label htmlFor="freq-one_time" className="flex items-center gap-2 cursor-pointer flex-1">
                          <CreditCard className="w-5 h-5 text-blue-500" />
                          <div>
                            <p className="font-medium">Paiement unique</p>
                            <p className="text-xs text-gray-500">{montant.toFixed(2)}€ en une fois</p>
                          </div>
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                        <RadioGroupItem value="monthly" id="freq-monthly" />
                        <Label htmlFor="freq-monthly" className="flex items-center gap-2 cursor-pointer flex-1">
                          <CalendarDays className="w-5 h-5 text-purple-500" />
                          <div>
                            <p className="font-medium">Mensuel</p>
                            <p className="text-xs text-gray-500">{(montant / 12).toFixed(2)}€/mois × 12 mois (engagement minimum)</p>
                          </div>
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                        <RadioGroupItem value="annual" id="freq-annual" />
                        <Label htmlFor="freq-annual" className="flex items-center gap-2 cursor-pointer flex-1">
                          <Repeat className="w-5 h-5 text-green-500" />
                          <div>
                            <p className="font-medium">Annuel</p>
                            <p className="text-xs text-gray-500">{montant.toFixed(2)}€/an (renouvelé automatiquement)</p>
                          </div>
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* Montant récapitulatif */}
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="text-gray-600">Montant à régler</span>
                        <p className="text-xs text-gray-500">{displayAmount.description}</p>
                      </div>
                      <span className="text-2xl font-bold text-blue-600">{displayAmount.label}</span>
                    </div>
                    {paymentFrequency !== 'one_time' && (
                      <p className="text-xs text-blue-600 mt-2 flex items-center gap-1">
                        <Repeat className="w-3 h-3" />
                        Total sur la période: {montant.toFixed(2)}€
                      </p>
                    )}
                  </div>

                  {/* Choix méthode de paiement */}
                  <div className="space-y-3">
                    <Label className="font-semibold">Mode de paiement</Label>
                    <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                      <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                        <RadioGroupItem value="stripe" id="stripe" />
                        <Label htmlFor="stripe" className="flex items-center gap-2 cursor-pointer flex-1">
                          <CreditCard className="w-5 h-5 text-blue-500" />
                          <div>
                            <p className="font-medium">Carte bancaire</p>
                            <p className="text-xs text-gray-500">
                              {paymentFrequency !== 'one_time' 
                                ? 'Prélèvement automatique via Stripe' 
                                : 'Paiement sécurisé via Stripe'}
                            </p>
                          </div>
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                        <RadioGroupItem value="virement" id="virement" />
                        <Label htmlFor="virement" className="flex items-center gap-2 cursor-pointer flex-1">
                          <Building2 className="w-5 h-5 text-green-500" />
                          <div>
                            <p className="font-medium">Virement bancaire</p>
                            <p className="text-xs text-gray-500">
                              {paymentFrequency !== 'one_time' 
                                ? 'Virement à effectuer chaque période' 
                                : 'Validation manuelle sous 48h'}
                            </p>
                          </div>
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* Infos bancaires si virement sélectionné et généré */}
                  {showBankInfo && bankInfo && (
                    <div className="bg-gray-50 p-4 rounded-lg space-y-2 border">
                      <div className="flex justify-between items-center mb-2">
                        <p className="font-semibold">Informations de virement</p>
                        <Button variant="ghost" size="sm" onClick={copyBankInfo}>
                          <Copy className="w-4 h-4 mr-1" />
                          Copier
                        </Button>
                      </div>
                      <div className="text-sm space-y-1">
                        <p><span className="text-gray-500">Bénéficiaire:</span> {bankInfo.beneficiaire}</p>
                        <p><span className="text-gray-500">IBAN:</span> <code className="bg-white px-1 rounded">{bankInfo.iban}</code></p>
                        <p><span className="text-gray-500">BIC:</span> {bankInfo.bic}</p>
                        <p><span className="text-gray-500">Banque:</span> {bankInfo.banque}</p>
                        <p className="pt-2 border-t">
                          <span className="text-gray-500">Référence à indiquer:</span>{' '}
                          <Badge variant="outline" className="font-mono">{bankInfo.reference}</Badge>
                        </p>
                        <p>
                          <span className="text-gray-500">Montant:</span>{' '}
                          <strong>{bankInfo.montant} €</strong>
                        </p>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Section Partage */}
          {(activeTab === 'share' || montant === 0) && (
            <div className="space-y-4">
              <div className="space-y-3">
                <Label className="font-semibold">Partager vers</Label>
                <RadioGroup value={shareMethod} onValueChange={setShareMethod}>
                  <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                    <RadioGroupItem value="email" id="share-email" data-testid="convention-share-option-email" />
                    <Label htmlFor="share-email" className="flex items-center gap-2 cursor-pointer flex-1">
                      <Mail className="w-5 h-5 text-blue-500" />
                      <div>
                        <p className="font-medium">Par email</p>
                        <p className="text-xs text-gray-500">Envoyer un lien par email</p>
                      </div>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                    <RadioGroupItem value="member_space" id="share-member" data-testid="convention-share-option-member" />
                    <Label htmlFor="share-member" className="flex items-center gap-2 cursor-pointer flex-1">
                      <UserCircle className="w-5 h-5 text-green-500" />
                      <div>
                        <p className="font-medium">Espace Membre</p>
                        <p className="text-xs text-gray-500">Visible dans l'espace du membre</p>
                      </div>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                    <RadioGroupItem value="cooper_space" id="share-cooper" data-testid="convention-share-option-cooper" />
                    <Label htmlFor="share-cooper" className="flex items-center gap-2 cursor-pointer flex-1">
                      <Users className="w-5 h-5 text-purple-500" />
                      <div>
                        <p className="font-medium">Espace COOPER'S</p>
                        <p className="text-xs text-gray-500">Visible dans l'espace COOPER'S</p>
                      </div>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                    <RadioGroupItem value="expert_space" id="share-expert" data-testid="convention-share-option-expert" />
                    <Label htmlFor="share-expert" className="flex items-center gap-2 cursor-pointer flex-1">
                      <Briefcase className="w-5 h-5 text-orange-500" />
                      <div>
                        <p className="font-medium">Espace Expert</p>
                        <p className="text-xs text-gray-500">Visible dans l'espace Expert</p>
                      </div>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {shareMethod === 'email' && (
                <div>
                  <Label htmlFor="share-email-input">Adresse email</Label>
                  <Input
                    id="share-email-input"
                    data-testid="convention-share-email-input"
                    type="email"
                    value={shareEmail}
                    onChange={(e) => setShareEmail(e.target.value)}
                    placeholder="destinataire@email.com"
                    className="mt-1"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" data-testid="convention-share-close-btn" onClick={() => onOpenChange(false)}>
            Fermer
          </Button>
          
          {activeTab === 'payment' && montant > 0 && !isPaid && !isPendingVirement && (
            paymentMethod === 'stripe' ? (
              <Button 
                onClick={handleStripeCheckout} 
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CreditCard className="w-4 h-4 mr-2" />
                )}
                Payer {montant.toFixed(2)} €
              </Button>
            ) : (
              <Button 
                onClick={handleInitiateVirement} 
                disabled={loading || showBankInfo}
                className="bg-green-600 hover:bg-green-700"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Building2 className="w-4 h-4 mr-2" />
                )}
                {showBankInfo ? 'Virement initié' : 'Obtenir les coordonnées bancaires'}
              </Button>
            )
          )}
          
          {(activeTab === 'share' || montant === 0) && (
            <Button 
              onClick={handleShare} 
              data-testid="convention-share-submit-btn"
              disabled={loading || (shareMethod === 'email' && !shareEmail)}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Partager
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
